// ITEM 12 (her `10`) — **THE MAGIC ROOM'S FILE CABINET, STYLED AS ROOM FURNITURE.**
//
// Her item: *"Greater Keys ordered by PLANET then NUMBER (4th and 6th Saturn absent, 7th present).
// The Sixth Book's seven complete. The Seventh Book honestly awaiting her plates. Seals of God:
// download and save-to-journal ONLY. Per-seal actions: download print quality, save to journal, send
// to cauldron. Verbatim laws. Ban laws at EVERY layer. Styled as room furniture."*
//
// **EVERY DECISION IN HERE IS `src/fileCabinet.js`'s, IMPORTED.** Which drawers exist, what order the
// Greater Keys are in, which pentacles may never appear, what may be DONE with a row, what a journal
// entry says and what rides to the Cauldron — all of it is a `.js` law a FAST gate RUNS, because a
// cabinet in the wrong order and a cabinet missing a control both render perfectly (R8/R95).
//
// **`CabinetItemCard` AND `CabinetDrawerBody` ARE HOOKLESS ON PURPOSE.** Props in, tree out, no
// state, no hook — the entry requirement that lets `scripts/file-cabinet.mjs` CALL them and read the
// tree back, over every state a row can be in: a pentacle with its pairing, a Seal of God (whose
// card must carry NO Send to the Cauldron), a Seventh Book table with no plate and no actions at all,
// and a drawer that has not answered yet. The action STATE lives up in `FileCabinet`, which is what
// keeps the card callable — a chip that owned its own `useState` would put this whole surface back
// out of a gate's reach.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, NotebookPen, FlaskConical } from "lucide-react";
import {
  CABINET_DRAWERS,
  cabinetActionsFor,
  cabinetCauldronSend,
  cabinetDownloadName,
  cabinetDrawerState,
  cabinetJournalEntry,
  orderGreaterKeys,
  withoutBannedPentacles,
  DRAWER_MOSES_SEVENTH,
} from "../fileCabinet.js";
import { writeJournalEntry, journalNote, JOURNAL_CHANGED_EVENT } from "../lib/journal";
// IT #198/#199 — the plate is SAVED through the one transport, never handed to the OS by an anchor.
import { downloadFromUrl } from "../lib/downloads";
import { sendToCauldron, cauldronSendNote, cauldronSendState } from "../lib/cauldron-send";
import { GoToCauldronButton } from "./CauldronDoor";
import { t } from "../strings.js";

export type CabinetItem = {
  key: string;
  drawer: string;
  slug: string | null;
  name: string;
  planet: string | null;
  ordinal: number | null;
  figure: number | null;
  purpose: string;
  verseRef: string;
  verseText: string;
  waysToUse: string[];
  sealOfGod: { name: string; slug: string; use: string } | null;
  imageUrl: string | null;
};

type ChipState = "idle" | "busy" | "done" | "error";
type ActionState = { journal: ChipState; cauldron: ChipState; note: string };
const IDLE: ActionState = { journal: "idle", cauldron: "idle", note: "" };

// The line under a row's name: "Jupiter · 3" for a pentacle, "Fig. 20" for a plate, nothing for a
// Seal of God (which has neither, and an invented one would be the first fabricated fact in a
// cabinet whose whole claim is that nothing in it was composed).
export function cabinetItemCaption(item: CabinetItem): string {
  const bits: string[] = [];
  if (item.planet) bits.push(item.planet.charAt(0).toUpperCase() + item.planet.slice(1).toLowerCase());
  if (item.ordinal) bits.push(String(item.ordinal));
  if (item.figure) bits.push(`Fig. ${item.figure}`);
  return bits.join(" · ");
}

// ONE ROW, OPEN. Hookless.
export function CabinetItemCard({
  item, state, onJournal, onCauldron, language,
}: {
  item: CabinetItem;
  state: ActionState;
  onJournal: (item: CabinetItem) => void;
  onCauldron: (item: CabinetItem) => void;
  language: string;
}) {
  // HER LAW, ASKED OF THE LAW RATHER THAN WRITTEN OUT HERE. A Seal of God answers two actions and a
  // plateless table answers none, and neither of those is a condition typed at this call site.
  const actions = cabinetActionsFor(item);
  return (
    <article className="cabinet-card">
      {item.imageUrl ? (
        <img className="cabinet-card__plate" src={item.imageUrl} alt={item.name} loading="lazy" />
      ) : (
        // The Seventh Book, honestly. A missing plate says so in words rather than drawing a broken
        // picture or an empty frame that reads as a load that failed.
        <p className="cabinet-card__awaiting">This table is catalogued and its plate is not here yet.</p>
      )}
      <div className="cabinet-card__body">
        <h4 className="cabinet-card__name">{item.name}</h4>
        {cabinetItemCaption(item) ? <p className="cabinet-card__caption">{cabinetItemCaption(item)}</p> : null}
        {/* VERBATIM: the source's own purpose line. Nothing here rewrites it. */}
        {item.purpose ? <p className="cabinet-card__purpose">{item.purpose}</p> : null}
        {item.verseText ? (
          <p className="cabinet-card__verse">
            {item.verseRef ? <span className="cabinet-card__verse-ref">{item.verseRef}</span> : null}
            <span>&ldquo;{item.verseText}&rdquo;</span>
          </p>
        ) : null}
        {item.waysToUse.length ? (
          <div className="cabinet-card__ways">
            <span className="cabinet-card__ways-title">Ways to use</span>
            <ul>{item.waysToUse.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        ) : null}
        {item.sealOfGod ? (
          <p className="cabinet-card__pairing">
            <span className="cabinet-card__pairing-name">{item.sealOfGod.name}</span>
            {item.sealOfGod.use ? <span>{item.sealOfGod.use}</span> : null}
          </p>
        ) : null}
        {actions.length ? (
          <div className="cabinet-card__actions">
            {/* The plate as the house holds it, at its native size, saved rather than opened.
                Nothing is resampled: an enlarged seal is invented detail.
                >>> **IT #198/#199 — A BUTTON, NEVER AN ANCHOR.** <<< A bare `<a download>` on a phone
                is a handoff to the OS, which gives the file to whatever app claims the type -- an
                astrology app, for her -- and the seal is gone. The ONE transport instead (R28/R29;
                `SealCard` learned this at #127). */}
            {actions.includes("download") && item.imageUrl ? (
              <button
                type="button"
                className="button button--ghost"
                onClick={() => { void downloadFromUrl(item.imageUrl as string); }}
              >
                <Download size={15} /> Download the plate
              </button>
            ) : null}
            {actions.includes("journal") ? (
              <button type="button" className="button button--ghost" onClick={() => onJournal(item)}
                disabled={state.journal === "busy" || state.journal === "done"}>
                <NotebookPen size={15} />
                {state.journal === "done" ? "✓ In your journal" : state.journal === "busy" ? "Saving..." : "Save to journal"}
              </button>
            ) : null}
            {actions.includes("cauldron") ? (
              <button type="button" className="button button--ghost" onClick={() => onCauldron(item)}
                disabled={state.cauldron === "busy" || state.cauldron === "done"}>
                <FlaskConical size={15} />
                {t(language, state.cauldron === "done" ? "cauldron.sent" : state.cauldron === "busy" ? "cauldron.sending" : "cauldron.send")}
              </button>
            ) : null}
            {state.cauldron === "done" ? <GoToCauldronButton className="button button--ghost" language={language} /> : null}
          </div>
        ) : null}
        {state.note ? <p className="cabinet-card__status" role="status">{state.note}</p> : null}
      </div>
    </article>
  );
}

// ONE DRAWER, OPEN. Hookless.
export function CabinetDrawerBody({
  drawerKey, note, items, failed, openKey, states, onOpenItem, onJournal, onCauldron, language,
}: {
  drawerKey: string;
  note: string;
  items: CabinetItem[] | null;
  failed: boolean;
  openKey: string;
  states: Record<string, ActionState>;
  onOpenItem: (key: string) => void;
  onJournal: (item: CabinetItem) => void;
  onCauldron: (item: CabinetItem) => void;
  language: string;
}) {
  // FOUR STATES, NEVER TWO (R18/R64). A drawer that has not answered HOLDS STILL rather than
  // telling her a cabinet that has held forty-two seals since the room opened is empty.
  const state = cabinetDrawerState({ failed, items });
  return (
    <div className="cabinet-drawer__body">
      <p className="cabinet-drawer__note">{note}</p>
      {state === "waiting" ? <p className="cabinet-drawer__status">Opening the drawer...</p> : null}
      {state === "failed" ? <p className="cabinet-drawer__status">This drawer could not be opened just now.</p> : null}
      {state === "empty" ? <p className="cabinet-drawer__status">Nothing is filed in here yet.</p> : null}
      {state === "list" ? (
        <ul className="cabinet-rows">
          {(items as CabinetItem[]).map((item) => (
            <li key={item.key} className={`cabinet-row${openKey === item.key ? " is-open" : ""}`}>
              <button type="button" className="cabinet-row__tab" onClick={() => onOpenItem(item.key)}
                aria-expanded={openKey === item.key}>
                {item.imageUrl ? <img className="cabinet-row__thumb" src={item.imageUrl} alt="" aria-hidden="true" loading="lazy" /> : <span className="cabinet-row__thumb cabinet-row__thumb--none" aria-hidden="true" />}
                <span className="cabinet-row__name">{item.name}</span>
                {cabinetItemCaption(item) ? <span className="cabinet-row__caption">{cabinetItemCaption(item)}</span> : null}
              </button>
              {openKey === item.key ? (
                <CabinetItemCard item={item} state={states[item.key] || IDLE} onJournal={onJournal} onCauldron={onCauldron} language={language} />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// THE CABINET ITSELF. The one stateful piece: the read, which drawer is out, which row is open, and
// what each row's two chips are doing.
export function FileCabinet({ language }: { language: string }) {
  const [drawers, setDrawers] = useState<Record<string, CabinetItem[]> | null>(null);
  const [failed, setFailed] = useState(false);
  const [openDrawer, setOpenDrawer] = useState<string>(CABINET_DRAWERS[0].key);
  const [openKey, setOpenKey] = useState<string>("");
  const [states, setStates] = useState<Record<string, ActionState>>({});
  // D-5's law, one room over: a `useState` guard is read from the render that BOUND the handler, so
  // two taps inside one frame both send. What the member SEES is the disabled chip; what actually
  // refuses the second write is this ref.
  const inFlight = useRef<Set<string>>(new Set());

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch("/api/cabinet", { cache: "no-store" });
        const data = await res.json().catch(() => ({}));
        if (!live) return;
        // A refusal is a refusal, never an empty cabinet (R18) — the route answers 500 `ok:false`
        // when it could not read, and painting that as four empty drawers is the lie this house has
        // now caught on four other routes.
        if (!res.ok || data?.ok === false || !data?.drawers) { setFailed(true); return; }
        setDrawers(data.drawers as Record<string, CabinetItem[]>);
      } catch {
        if (live) setFailed(true);
      }
    })();
    return () => { live = false; };
  }, []);

  // B1's LAST LAYER, on the screen itself. The SQL cannot send one, the store filters one, the route
  // filters one — and this filters one, so her "ban laws at EVERY layer" clause is true of the render
  // as well. The ORDER is re-asserted here for the same reason: the law is the law wherever it runs.
  const rowsFor = useCallback((key: string): CabinetItem[] | null => {
    if (!drawers) return null;
    const raw = withoutBannedPentacles(drawers[key] || []) as CabinetItem[];
    return key === CABINET_DRAWERS[0].key ? (orderGreaterKeys(raw) as CabinetItem[]) : raw;
  }, [drawers]);

  const counts = useMemo(() => {
    const out: Record<string, number | null> = {};
    for (const d of CABINET_DRAWERS) { const rows = rowsFor(d.key); out[d.key] = rows ? rows.length : null; }
    return out;
  }, [rowsFor]);

  const setChip = (key: string, patch: Partial<ActionState>) =>
    setStates((s) => ({ ...s, [key]: { ...(s[key] || IDLE), ...patch } }));

  async function onJournal(item: CabinetItem) {
    const guard = `${item.key}:journal`;
    if (inFlight.current.has(guard)) return;
    inFlight.current.add(guard);
    setChip(item.key, { journal: "busy", note: "" });
    try {
      const draft = cabinetJournalEntry(item);
      const answer = await writeJournalEntry(draft as { title: string; body: string });
      if (answer.ok) window.dispatchEvent(new Event(JOURNAL_CHANGED_EVENT));
      setChip(item.key, {
        journal: answer.ok ? "done" : "error",
        note: journalNote(answer, { signIn: t(language, "journal.signIn"), failed: t(language, "journal.saveFailed") }),
      });
    } finally {
      inFlight.current.delete(guard);
    }
  }

  async function onCauldron(item: CabinetItem) {
    const guard = `${item.key}:cauldron`;
    if (inFlight.current.has(guard)) return;
    const body = cabinetCauldronSend(item);
    // The law refuses a Seal of God and a plateless table. A control that should not exist cannot
    // send even if one somehow reached the screen.
    if (!body) return;
    inFlight.current.add(guard);
    setChip(item.key, { cauldron: "busy", note: "" });
    try {
      const answer = await sendToCauldron(body as { kind: string; title: string; data: unknown });
      setChip(item.key, {
        cauldron: cauldronSendState(answer) === "sent" ? "done" : "error",
        note: cauldronSendNote(answer, { signIn: t(language, "cauldron.sendSignIn"), failed: t(language, "cauldron.sendFailed") }),
      });
    } finally {
      inFlight.current.delete(guard);
    }
  }

  return (
    <div className="cabinet">
      <div className="cabinet__frame">
        {CABINET_DRAWERS.map((d) => {
          const out = openDrawer === d.key;
          const n = counts[d.key];
          return (
            <section key={d.key} className={`cabinet-drawer${out ? " is-out" : ""}${d.key === DRAWER_MOSES_SEVENTH ? " cabinet-drawer--awaiting" : ""}`}>
              <h3 className="cabinet-drawer__front">
                <button type="button" className="cabinet-drawer__pull" aria-expanded={out}
                  onClick={() => { setOpenDrawer(out ? "" : d.key); setOpenKey(""); }}>
                  <span className="cabinet-drawer__handle" aria-hidden="true" />
                  <span className="cabinet-drawer__label">{d.label}</span>
                  {/* A count that has not arrived shows NOTHING rather than a zero — R18 again, on a
                      number this time: "no seals in here" and "we have not asked yet" are two facts. */}
                  {typeof n === "number" ? <span className="cabinet-drawer__count">{n}</span> : null}
                </button>
              </h3>
              {out ? (
                <CabinetDrawerBody
                  drawerKey={d.key} note={d.note} items={rowsFor(d.key)} failed={failed}
                  openKey={openKey} states={states}
                  onOpenItem={(k) => setOpenKey((cur) => (cur === k ? "" : k))}
                  onJournal={onJournal} onCauldron={onCauldron} language={language}
                />
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
